/**
 * Arrange-phase execution engine.
 * 
 * Provides a unified API for running any prioritization tool using only canonical types.
 * Orchestrates the tool pipeline: items → validated → executed → canonical scores → arranged results.
 * 
 * This engine is stateless and framework-agnostic. It relies on the harmonizer module
 * for tool discovery and adapter management.
 */

import {
  getAdapterByName,
  type HarmonizedToolAdapter,
  type Item,
  type CriteriaSet,
  type ToolConfig,
  type Lens,
  type CanonicalScoreSet,
} from "../harmonizer";
import {
  validateItemsForTool,
  validateCriteriaSetForTool,
  validateToolConfigForTool,
  applyLens,
} from "./validation";

/**
 * Error class for Arrange-phase execution errors.
 * Provides structured error information for debugging and error handling.
 */
export class ArrangeEngineError extends Error {
  public readonly toolName: string;
  public readonly phase: "lookup" | "validation" | "execution";
  public readonly cause?: Error;

  constructor(
    message: string,
    toolName: string,
    phase: "lookup" | "validation" | "execution",
    cause?: Error
  ) {
    super(message);
    this.name = "ArrangeEngineError";
    this.toolName = toolName;
    this.phase = phase;
    this.cause = cause;
  }
}

/**
 * Runs a prioritization tool using canonical types.
 * 
 * Pipeline:
 * 1. Lookup adapter via getAdapterByName
 * 2. Validate Item structure against adapter.inputContract
 * 3. Validate CriteriaSet if required
 * 4. Validate ToolConfig against adapter.toolConfigFields
 * 5. Apply Lens filter to items
 * 6. Execute the tool via adapter.runTool
 * 7. Return canonical result
 * 
 * @param toolName - Name of the tool to run (must match adapter.toolName)
 * @param items - Items to prioritize
 * @param criteriaSet - CriteriaSet for evaluation (null if not required)
 * @param toolConfig - Tool-specific configuration
 * @param lens - Optional Lens for filtering/sorting items
 * @returns CanonicalScoreSet with tool execution results
 * @throws ArrangeEngineError if any phase fails
 */
export async function runArrangeTool(
  toolName: string,
  items: Item[],
  criteriaSet: CriteriaSet | null,
  toolConfig: ToolConfig,
  lens: Lens | null
): Promise<CanonicalScoreSet> {
  // Phase 1: Lookup adapter
  let adapter: HarmonizedToolAdapter | undefined;
  try {
    adapter = getAdapterByName(toolName);
    if (!adapter) {
      throw new ArrangeEngineError(
        `Tool '${toolName}' not found in registry.`,
        toolName,
        "lookup"
      );
    }
  } catch (error) {
    if (error instanceof ArrangeEngineError) {
      throw error;
    }
    throw new ArrangeEngineError(
      `Failed to lookup tool '${toolName}': ${error instanceof Error ? error.message : String(error)}`,
      toolName,
      "lookup",
      error instanceof Error ? error : undefined
    );
  }

  // Phase 2: Validate Items
  try {
    validateItemsForTool(items, adapter);
  } catch (error) {
    throw new ArrangeEngineError(
      `Item validation failed: ${error instanceof Error ? error.message : String(error)}`,
      toolName,
      "validation",
      error instanceof Error ? error : undefined
    );
  }

  // Phase 3: Validate CriteriaSet
  try {
    validateCriteriaSetForTool(criteriaSet, adapter);
  } catch (error) {
    throw new ArrangeEngineError(
      `CriteriaSet validation failed: ${error instanceof Error ? error.message : String(error)}`,
      toolName,
      "validation",
      error instanceof Error ? error : undefined
    );
  }

  // Phase 4: Validate ToolConfig
  try {
    validateToolConfigForTool(toolConfig, adapter);
  } catch (error) {
    throw new ArrangeEngineError(
      `ToolConfig validation failed: ${error instanceof Error ? error.message : String(error)}`,
      toolName,
      "validation",
      error instanceof Error ? error : undefined
    );
  }

  // Phase 5: Check Lens requirement
  if (adapter.inputContract.lensRequired && !lens) {
    throw new ArrangeEngineError(
      `Tool '${toolName}' requires a Lens, but none was provided.`,
      toolName,
      "validation"
    );
  }

  // Phase 6: Apply Lens filter
  let filteredItems = items;
  if (lens) {
    try {
      filteredItems = applyLens(items, lens);
      if (filteredItems.length === 0) {
        throw new ArrangeEngineError(
          `Lens filter resulted in zero items for tool '${toolName}'.`,
          toolName,
          "validation"
        );
      }
    } catch (error) {
      if (error instanceof ArrangeEngineError) {
        throw error;
      }
      throw new ArrangeEngineError(
        `Failed to apply Lens: ${error instanceof Error ? error.message : String(error)}`,
        toolName,
        "validation",
        error instanceof Error ? error : undefined
      );
    }
  }

  // Phase 7: Execute tool
  try {
    const result = await adapter.runTool(
      filteredItems,
      criteriaSet,
      toolConfig,
      lens
    );

    // Validate result structure (basic check)
    if (!result || !result.toolRunId || !result.scores) {
      throw new ArrangeEngineError(
        `Tool '${toolName}' returned invalid CanonicalScoreSet: missing required fields.`,
        toolName,
        "execution"
      );
    }

    // Ensure toolName and toolCategory match adapter
    if (result.toolName !== adapter.toolName) {
      throw new ArrangeEngineError(
        `Tool '${toolName}' returned CanonicalScoreSet with mismatched toolName '${result.toolName}'.`,
        toolName,
        "execution"
      );
    }

    if (result.toolCategory !== adapter.category) {
      throw new ArrangeEngineError(
        `Tool '${toolName}' returned CanonicalScoreSet with mismatched toolCategory '${result.toolCategory}' (expected '${adapter.category}').`,
        toolName,
        "execution"
      );
    }

    return result;
  } catch (error) {
    if (error instanceof ArrangeEngineError) {
      throw error;
    }
    throw new ArrangeEngineError(
      `Tool execution failed: ${error instanceof Error ? error.message : String(error)}`,
      toolName,
      "execution",
      error instanceof Error ? error : undefined
    );
  }
}

